import { useUserProfile } from '@/contexts/UserProfileContext';
import {
  formatDateInUserTimezone,
  formatRelativeTimeInUserTimezone,
  convertUTCToUserTimezone,
} from '@/utils/dateUtils';

/**
 * Hook for formatting dates in user's timezone
 * Automatically uses the user's timezone from profile
 *
 * @example
 * const { formatDate, formatRelativeTime } = useDateFormatter();
 * formatDate("2025-01-08T12:00:00Z"); // Returns formatted date in user's timezone
 * formatRelativeTime("2025-01-08T10:00:00Z"); // Returns "2h atrás"
 */
export function useDateFormatter() {
  const { userData } = useUserProfile();
  const userTimezone = userData?.timezone || 'UTC';

  return {
    /**
     * Format UTC date for display in user's timezone
     * @param utcDateString - ISO 8601 string from API
     * @param formatString - Optional format (default: 'PPp')
     * @param locale - Optional locale (default: 'pt-BR')
     *
     * @example
     * formatDate("2025-01-08T12:00:00Z") // "8 de jan. de 2025 12:00"
     * formatDate("2025-01-08T12:00:00Z", "dd/MM/yyyy") // "08/01/2025"
     * formatDate("2025-01-08T12:00:00Z", "dd/MM/yyyy HH:mm") // "08/01/2025 12:00"
     */
    formatDate: (
      utcDateString: string | null | undefined,
      formatString?: string,
      locale?: 'pt-BR' | 'en-US' | 'es-ES'
    ) => {
      return formatDateInUserTimezone(utcDateString, userTimezone, formatString, locale);
    },

    /**
     * Format UTC date as relative time (e.g., "2h ago") in user's timezone
     * @param utcDateString - ISO 8601 string from API
     *
     * @example
     * formatRelativeTime("2025-01-08T10:00:00Z") // "2h atrás"
     */
    formatRelativeTime: (utcDateString: string | null | undefined) => {
      return formatRelativeTimeInUserTimezone(utcDateString, userTimezone);
    },

    /**
     * Convert UTC to user's timezone Date object
     * @param utcDateString - ISO 8601 string from API
     *
     * @example
     * convertToUserTimezone("2025-01-08T12:00:00Z") // Date object in user's timezone
     */
    convertToUserTimezone: (utcDateString: string | null | undefined) => {
      return convertUTCToUserTimezone(utcDateString, userTimezone);
    },

    /**
     * Get user's current timezone
     *
     * @example
     * userTimezone // "Europe/Lisbon"
     */
    userTimezone,

    /**
     * Format UTC date as date only (dd/MM/yyyy) in user's timezone
     * @param utcDateString - ISO 8601 string from API
     *
     * @example
     * formatDateOnly("2025-01-08T12:00:00Z") // "08/01/2025"
     */
    formatDateOnly: (utcDateString: string | null | undefined) => {
      return formatDateInUserTimezone(utcDateString, userTimezone, 'dd/MM/yyyy');
    },

    /**
     * Format UTC date with time (dd/MM/yyyy HH:mm) in user's timezone
     * @param utcDateString - ISO 8601 string from API
     *
     * @example
     * formatDateTime("2025-01-08T14:30:00Z") // "08/01/2025 14:30"
     */
    formatDateTime: (utcDateString: string | null | undefined) => {
      return formatDateInUserTimezone(utcDateString, userTimezone, 'dd/MM/yyyy HH:mm');
    },

    /**
     * Format UTC date as short format (d MMM yyyy) in user's timezone
     * @param utcDateString - ISO 8601 string from API
     *
     * @example
     * formatDateShort("2025-01-08T12:00:00Z") // "8 jan. 2025"
     */
    formatDateShort: (utcDateString: string | null | undefined) => {
      return formatDateInUserTimezone(utcDateString, userTimezone, 'd MMM yyyy');
    },

    /**
     * Format UTC date as time only (HH:mm) in user's timezone
     * @param utcDateString - ISO 8601 string from API
     *
     * @example
     * formatTime("2025-01-08T14:30:00Z") // "14:30"
     */
    formatTime: (utcDateString: string | null | undefined) => {
      return formatDateInUserTimezone(utcDateString, userTimezone, 'HH:mm');
    },

    /**
     * Format UTC date for HTML date inputs (YYYY-MM-DD) in user's timezone
     * Used for pre-filling <input type="date"> elements
     *
     * @param utcDateString - ISO 8601 string from API
     * @returns Date string in YYYY-MM-DD format, or empty string if null/undefined
     *
     * @example
     * formatDateForInput("2025-10-01T00:00:00.000000Z") // "2025-10-01"
     * formatDateForInput(null) // ""
     */
    formatDateForInput: (utcDateString: string | null | undefined) => {
      if (!utcDateString) return '';

      try {
        const zonedDate = convertUTCToUserTimezone(utcDateString, userTimezone);
        if (!zonedDate) return '';

        // Format as YYYY-MM-DD for HTML date input
        const year = zonedDate.getFullYear();
        const month = String(zonedDate.getMonth() + 1).padStart(2, '0');
        const day = String(zonedDate.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('Error formatting date for input:', error);
        return '';
      }
    },
  };
}
